// ============================================
// FullChatView Component
// Full-screen chat view like Messenger/WhatsApp
// ============================================

import React, { useState, useEffect } from 'react';
import { useSession } from '@/context/SessionContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useChatConversations, useChatMessages } from '@/hooks/useChat';
import { useChatContext } from '@/context/ChatContext';
import { joinGroupByInviteToken, getUserGroupChats } from '@/lib/supabase/chat';
import { supabase } from '@/lib/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  Search,
  MoreVertical,
  Info,
  Send,
  X,
  Mail,
  Users,
  FileText,
  MessageCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getAvatarInitials } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { MessageInput } from './MessageInput';
import { GroupInfoModal } from './GroupInfoModal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export const FullChatView: React.FC = () => {
  const location = useLocation();
  const { session } = useSession();
  const navigate = useNavigate();
  const { conversations, groups, loading, refetch } = useChatConversations();
  const { closeChatWindow, openChatWindow } = useChatContext();
  const { toast } = useToast();
  const [activeChat, setActiveChat] = useState<{
    type: 'dm' | 'group';
    id: string;
    conversationId?: string;
    groupId?: string;
    otherUserId?: string;
    title: string;
    avatarUrl?: string;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showConversation, setShowConversation] = useState(false);
  const [activeTab, setActiveTab] = useState<'dms' | 'groups'>('dms');
  const [isGroupInfoModalOpen, setIsGroupInfoModalOpen] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [processedInviteToken, setProcessedInviteToken] = useState<string | null>(null);
  const [storeAvatarById, setStoreAvatarById] = useState<Record<string, string>>({});
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;

  const getStoreIdFromGroupDescription = (description?: string | null): string | null => {
    if (!description) return null;
    const match = description.trim().match(/^store-group:([0-9a-fA-F-]{36})$/);
    return match?.[1] || null;
  };

  // Get messages for active chat
  const { messages, loading: messagesLoading, hasMore, loadMore, sendMessage } = useChatMessages({
    conversationId: activeChat?.conversationId,
    groupId: activeChat?.groupId,
  });

  // Auto-select chat if conversationId/groupId is passed in location.state
  React.useEffect(() => {
    const state = location.state as { conversationId?: string; groupId?: string } | undefined;
    if (state && (state.conversationId || state.groupId) && !activeChat) {
      if (state.conversationId) {
        const conv = conversations.find(c => c.id === state.conversationId);
        if (conv) handleSelectChat(conv, 'dm');
      } else if (state.groupId) {
        const group = groups.find(g => g.id === state.groupId);
        if (group) handleSelectChat(group, 'group');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, conversations, groups]);

  useEffect(() => {
    let cancelled = false;

    const loadStoreAvatarsForStoreGroups = async () => {
      const storeIds = Array.from(
        new Set(
          (groups || []).map((group: any) => group?.support_store_id || getStoreIdFromGroupDescription(group?.description)).filter(Boolean)
        )
      ) as string[];

      if (storeIds.length === 0) {
        setStoreAvatarById({});
        return;
      }

      const { data, error } = await supabase
        .from('stores')
        .select('id,avatar_url')
        .in('id', storeIds);

      if (error || cancelled) return;

      const nextMap: Record<string, string> = {};
      (data || []).forEach((store: any) => {
        if (store?.id && store?.avatar_url) {
          nextMap[store.id] = store.avatar_url;
        }
      });
      setStoreAvatarById(nextMap);
    };

    void loadStoreAvatarsForStoreGroups();

    return () => {
      cancelled = true;
    };
  }, [groups]);

  useEffect(() => {
    const inviteToken = new URLSearchParams(location.search).get('invite');
    if (!inviteToken || processedInviteToken === inviteToken) return;

    if (!session?.user?.id) {
      toast({
        title: 'Inicia sesión para unirte',
        description: 'Debes iniciar sesión para unirte al grupo con enlace.',
        variant: 'destructive',
      });
      setProcessedInviteToken(inviteToken);
      return;
    }

    let cancelled = false;
    const joinFromInvite = async () => {
      try {
        const joinedGroupId = await joinGroupByInviteToken(inviteToken);
        const freshGroups = await getUserGroupChats();
        const joinedGroup = freshGroups.find((group) => group.id === joinedGroupId);

        await refetch();

        if (!cancelled) {
          if (joinedGroup) {
            handleSelectChat(joinedGroup, 'group');
            setActiveTab('groups');
          }
          toast({
            title: 'Te uniste al grupo',
            description: 'Ya puedes chatear con el equipo.',
          });
          setProcessedInviteToken(inviteToken);
          navigate('/chat', { replace: true });
        }
      } catch (error: any) {
        if (!cancelled) {
          toast({
            title: 'No se pudo unir al grupo',
            description: error?.message || 'El enlace puede estar vencido o no ser válido.',
            variant: 'destructive',
          });
          setProcessedInviteToken(inviteToken);
          navigate('/chat', { replace: true });
        }
      }
    };

    void joinFromInvite();

    return () => {
      cancelled = true;
    };
  }, [location.search, navigate, processedInviteToken, refetch, session?.user?.id, toast]);

  // Filter conversations based on search
  const filteredConversations = conversations.filter(conv =>
    conv.other_username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.other_display_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredGroups = groups.filter(group =>
    group.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleBack = () => {
    navigate(-1);
  };

  const handleSelectChat = (chat: any, type: 'dm' | 'group') => {
    const supportStoreId = type === 'group'
      ? (chat?.support_store_id || getStoreIdFromGroupDescription(chat?.description))
      : null;
    const resolvedAvatarUrl = type === 'group'
      ? (supportStoreId ? (storeAvatarById[supportStoreId] || null) : chat.avatar_url)
      : chat.other_avatar_url;

    setActiveChat({
      type,
      id: chat.id,
      conversationId: type === 'dm' ? chat.id : undefined,
      groupId: type === 'group' ? chat.id : undefined,
      otherUserId: type === 'dm' ? chat.other_user_id : undefined,
      title: type === 'dm' ? (chat.other_display_name || chat.other_username) : chat.name,
      avatarUrl: resolvedAvatarUrl,
    });
    if (isMobile) {
      setShowConversation(true);
    }
  };

  const handleSendMessage = async (params: any) => {
    await sendMessage(params);
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Cargando chats...</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex bg-background overflow-hidden flex-col md:flex-row">
      {/* Sidebar - Chat List */}
      <div className={cn(
        "w-full md:w-80 border-r flex flex-col overflow-hidden",
        showConversation && isMobile && "hidden"
      )}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0 bg-primary text-white min-h-[64px]">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={handleBack} className="md:hidden text-white hover:bg-white/10 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-semibold text-lg">Mensajes</h1>
          </div>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar chats..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-9"
            />
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'dms' | 'groups')} className="flex flex-col flex-1 overflow-hidden">
          <TabsList className="grid grid-cols-2 mx-4 my-2 flex-shrink-0">
            <TabsTrigger value="dms" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Mensajes
            </TabsTrigger>
            <TabsTrigger value="groups" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Grupos
            </TabsTrigger>
          </TabsList>

          {/* DMs Tab */}
          <TabsContent value="dms" className="flex-1 overflow-hidden m-0">
            <ScrollArea className="h-full">
              <div className="p-2 pb-4">
                {filteredConversations.length === 0 ? (
                  <div className="text-center text-muted-foreground py-12 space-y-2">
                    <div className="text-3xl">💬</div>
                    <p className="font-semibold">¡Aún no hay conversaciones!</p>
                    <p className="text-sm">Regístrate y comienza a chatear con otros usuarios</p>
                  </div>
                ) : (
                  filteredConversations.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => handleSelectChat(conv, 'dm')}
                      className={cn(
                        'w-full p-3 rounded-lg text-left hover:bg-muted/50 transition-colors mb-1',
                        activeChat?.id === conv.id && activeChat.type === 'dm' && 'bg-muted'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={conv.other_avatar_url || undefined} />
                          <AvatarFallback>
                            {getAvatarInitials(conv.other_display_name || conv.other_username || '')}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="font-medium truncate">
                              {conv.other_display_name || conv.other_username}
                            </span>
                            {conv.last_message_at && (
                              <span className="text-xs text-muted-foreground">
                                {formatDistanceToNow(new Date(conv.last_message_at), {
                                  addSuffix: false,
                                  locale: es,
                                })}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-muted-foreground truncate">
                              {conv.last_message_content || 'No hay mensajes'}
                            </p>
                            {conv.unread_count > 0 && (
                              <Badge variant="default" className="h-5 min-w-5 px-1.5">
                                {conv.unread_count > 99 ? '99+' : conv.unread_count}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Groups Tab */}
          <TabsContent value="groups" className="flex-1 overflow-hidden m-0">
            <ScrollArea className="h-full">
              <div className="p-2 pb-4">
                {filteredGroups.length === 0 ? (
                  <div className="text-center text-muted-foreground py-12 space-y-2">
                    <div className="text-3xl">👥</div>
                    <p className="font-semibold">¡Sin grupos aún!</p>
                    <p className="text-sm">Únete a grupos cuando te registres</p>
                  </div>
                ) : (
                  filteredGroups.map((group) => (
                    (() => {
                      const supportStoreId = group?.support_store_id || getStoreIdFromGroupDescription(group?.description);
                      const groupAvatarUrl = supportStoreId
                        ? (storeAvatarById[supportStoreId] || null)
                        : group.avatar_url;
                      return (
                    <button
                      key={group.id}
                      onClick={() => handleSelectChat(group, 'group')}
                      className={cn(
                        'w-full p-3 rounded-lg text-left hover:bg-muted/50 transition-colors mb-1',
                        activeChat?.id === group.id && activeChat.type === 'group' && 'bg-muted'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="w-10 h-10 rounded-lg">
                          <AvatarImage src={groupAvatarUrl || undefined} />
                          <AvatarFallback className="rounded-lg">
                            {group.name.slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="font-medium truncate">{group.name}</span>
                            <div className="flex items-center gap-2">
                              {group.last_message_at && (
                                <span className="text-xs text-muted-foreground">
                                  {formatDistanceToNow(new Date(group.last_message_at), {
                                    addSuffix: false,
                                    locale: es,
                                  })}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-muted-foreground truncate">
                              {group.last_message_content || 'No hay mensajes'}
                            </p>
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary" className="text-xs px-1.5 py-0.5">
                                <Users className="w-3 h-3 mr-1" />
                                {group.member_count || 0}
                              </Badge>
                              {group.unread_count && group.unread_count > 0 && (
                                <Badge variant="destructive" className="text-xs px-1.5 py-0.5 min-w-[18px] h-[18px] flex items-center justify-center">
                                  {group.unread_count > 99 ? '99+' : group.unread_count}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                      );
                    })()
                  ))
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </div>

      {/* Main Chat Area */}
      <div className={cn(
        "flex-1 flex flex-col overflow-hidden",
        !showConversation && isMobile && "hidden"
      )}>
        {activeChat ? (
          <>
            {/* Chat Header */}
            <div className="px-4 py-3 border-b flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                {isMobile && (
                  <Button variant="ghost" size="icon" onClick={() => setShowConversation(false)}>
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                )}
                <Avatar className="w-10 h-10 rounded-lg">
                  <AvatarImage src={activeChat.avatarUrl || undefined} />
                  <AvatarFallback className="rounded-lg">
                    {activeChat.title?.slice(0, 2).toUpperCase() || '?'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h2 className="font-semibold">{activeChat.title}</h2>
                  <p className="text-sm text-muted-foreground">
                    {activeChat.type === 'dm' ? 'Mensaje directo' : 'Grupo'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {activeChat.type === 'group' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setSelectedGroupId(activeChat.groupId || '');
                      setIsGroupInfoModalOpen(true);
                    }}
                  >
                    <Info className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 overflow-hidden px-4 py-3">
              {messagesLoading ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground">Cargando mensajes...</p>
                </div>
              ) : messages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground text-center">
                    No hay mensajes aún.
                    <br />
                    ¡Envía el primero!
                  </p>
                </div>
              ) : (
                <div className="space-y-3 pb-2">
                  {messages.map((message, idx) => {
                    const currentUserId = session?.user?.id;
                    const isCurrentUser = message.sender_id === currentUserId;
                    const showAvatar = idx === messages.length - 1 || messages[idx + 1]?.sender_id !== message.sender_id;

                    return (
                      <div key={message.id} className={cn('flex gap-2', isCurrentUser ? 'justify-end' : 'justify-start')}>
                        {!isCurrentUser && showAvatar && (
                          <Avatar className="w-8 h-8 flex-shrink-0">
                            <AvatarImage src={message.sender_avatar_url || undefined} />
                            <AvatarFallback>
                              {(message.sender_display_name || message.sender_username || '?').slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                        )}
                        {!isCurrentUser && !showAvatar && <div className="w-8" />}

                        <div className={cn('max-w-[70%] rounded-2xl px-3 py-2', isCurrentUser ? 'bg-primary text-primary-foreground' : 'bg-muted')}>
                          {message.content && (
                            <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                          )}
                          {message.type === 'attachment' && message.attachment_url && (
                            <div className="mt-2">
                              {message.attachment_type?.startsWith('image/') ? (
                                <img
                                  src={message.attachment_url}
                                  alt={message.attachment_name || 'Imagen'}
                                  className="rounded-lg max-w-full max-h-64 object-contain"
                                  loading="lazy"
                                />
                              ) : (
                                <a
                                  href={message.attachment_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 p-2 bg-background/10 rounded hover:bg-background/20 transition-colors"
                                >
                                  <FileText className="w-4 h-4" />
                                  <span className="text-sm">{message.attachment_name || 'Archivo adjunto'}</span>
                                </a>
                              )}
                            </div>
                          )}
                          {message.is_edited && (
                            <span className="text-xs opacity-70 mt-1 block">(editado)</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>

            {/* Message Input */}
            <div className="px-4 py-3 border-t flex-shrink-0">
              <MessageInput
                conversationId={activeChat.conversationId}
                groupId={activeChat.groupId}
                recipientId={activeChat.otherUserId}
                onSend={handleSendMessage}
                onTyping={() => {}} // TODO: Implement typing
                autoFocus={true}
                disableAutoFocusOnMobile={false}
              />
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium mb-2">Selecciona un chat</h3>
              <p>Elige una conversación para empezar a chatear</p>
            </div>
          </div>
        )}
      </div>

      {/* Group Info Modal */}
      <GroupInfoModal
        isOpen={isGroupInfoModalOpen}
        onClose={() => setIsGroupInfoModalOpen(false)}
        groupId={selectedGroupId}
        initialAvatarUrl={activeChat?.type === 'group' ? (activeChat.avatarUrl || null) : null}
      />
    </div>
  );
};